import { useState } from 'react';
import { useT } from '@/store';
import {
  useAssignVehicleDriver,
  useComplianceCounts,
  useDeleteVehicle,
  useRestoreVehicle,
  useVehicles,
  VEHICLE_TYPE_REQUIRED_CATEGORY,
} from '@/hooks/useFleet';
import { useDrivers } from '@/hooks/useDirectory';
import { formatKm } from '@/lib/format';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { PaginationFooter, SearchInput } from '@/components/ui/TableToolbar';
import { useOffsetClamp, usePagedList } from '@/hooks/usePagedList';
import { SmartImportModal } from '@/components/data/SmartImportModal';
import { ArchivedBadge } from '@/components/ui/ArchivedBadge';
import { ComplianceBadges } from './ComplianceBadges';
import { VehicleFormModal } from './VehicleFormModal';
import { VehicleTypeIcon } from './VehicleTypeIcon';
import { DriverAssignField } from './DriverAssignField';
import { VehicleGpsReportModal } from './VehicleGpsReportModal';
import { GpsFleetReportModal } from './GpsFleetReportModal';
import { FleetIntegrityModal } from './FleetIntegrityModal';

const STATUS_TONE = {
  active: 'text-green',
  in_service: 'text-amber',
  decommissioned: 'text-dim',
};

// The icon swatch in a card's corner picks up the vehicle's status tone
// rather than its own fixed color, so the same glance that reads the plate
// also reads whether the truck is in a state worth noticing.
const ICON_TILE_TONE = {
  active: 'bg-accent/14 text-accent-hi',
  in_service: 'bg-amber/14 text-amber',
  decommissioned: 'bg-white/6 text-dim',
};

const KPI_ICON = {
  fleet: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 16V7a1 1 0 0 1 1-1h9v10" />
      <path d="M13 10h4.4a1 1 0 0 1 .8.4l2.4 3.1a1 1 0 0 1 .2.6V16h1" />
      <circle cx="7.5" cy="17.5" r="1.6" />
      <circle cx="17" cy="17.5" r="1.6" />
    </svg>
  ),
  road: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="18" r="2" />
      <path d="M8 6h6a4 4 0 0 1 4 4v0a4 4 0 0 1-4 4H10a4 4 0 0 0-4 4v0" />
    </svg>
  ),
  doc: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v4h4" />
      <path d="M9.5 13h5M9.5 16.5h5" />
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 2 20h20L12 3Z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="17" r="0.7" fill="currentColor" stroke="none" />
    </svg>
  ),
};

/**
 * The fleet: KPI row plus one card per vehicle. A grid rather than the
 * monolith's table, because the useful unit here is a whole vehicle — plate,
 * type, driver, three document states — and scanning that across a wide
 * table means reading horizontally through columns that are mostly badges.
 *
 * The compliance filters query the server (date arithmetic against its
 * clock, not the browser's — see useComplianceCounts), so a laptop with a
 * wrong system date cannot under-report how many trucks are about to fall
 * out of compliance.
 */
export function VehicleGrid() {
  const t = useT();
  const [statusFilter, setStatusFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('');
  // Off by default — and note the KPI row above deliberately keeps counting
  // only live vehicles either way, since a truck nobody drives anymore is not
  // part of the fleet total or of the expiring-documents figure.
  const [showArchived, setShowArchived] = useState(false);
  // null = closed, {} = adding, a vehicle row = editing that vehicle.
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importingGps, setImportingGps] = useState(false);
  // null = closed, a vehicle row = its GPS report is open. Separate from
  // `editing` -- clicking a plate opens the read-only report, the explicit
  // "Redaguoti" button still opens the edit form.
  const [gpsReportVehicle, setGpsReportVehicle] = useState(null);
  const [fleetGpsReportOpen, setFleetGpsReportOpen] = useState(false);
  const [fleetIntegrityOpen, setFleetIntegrityOpen] = useState(false);
  const { search, onSearchChange, limit, setLimit, offset, setOffset } = usePagedList();

  const { data: vehicles = [], totalCount, isLoading, isError, error } = useVehicles({
    status: statusFilter || undefined,
    expiringWithinDays: expiryFilter || undefined,
    search,
    limit,
    offset,
    includeInactive: showArchived || undefined,
  });
  // V-296. Deleting the last vehicle on a later page shrinks totalCount
  // without ever touching offset — without this, the grid reads a false
  // "no vehicles yet" over a fleet that still has trucks one page back.
  useOffsetClamp(totalCount, limit, offset, setOffset);
  const counts = useComplianceCounts();
  const { data: drivers = [] } = useDrivers();
  const assignDriver = useAssignVehicleDriver();
  const deleteVehicle = useDeleteVehicle();
  const restoreVehicle = useRestoreVehicle();

  // Share of the whole fleet, for the ring on each KPI card. Guarded against
  // a zero-vehicle fleet — with nothing to divide by, every ring reads empty
  // rather than NaN.
  const total = counts.total ?? 0;
  const pctOf = (n) => (total > 0 ? Math.round(((n ?? 0) / total) * 100) : 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          label={t('transportas.kpi.totalFleet')}
          value={counts.total}
          percent={100}
          ring="accent"
          icon={KPI_ICON.fleet}
        />
        <Kpi
          label={t('transportas.kpi.activeOnRoad')}
          value={counts.active}
          percent={pctOf(counts.active)}
          ring="green"
          icon={KPI_ICON.road}
        />
        <Kpi
          label={t('transportas.kpi.expiringDocs')}
          value={counts.expiring30}
          sub={t('transportas.kpi.within30d')}
          tone="text-amber"
          percent={pctOf(counts.expiring30)}
          ring="amber"
          icon={KPI_ICON.doc}
        />
        <Kpi
          label={t('transportas.kpi.critical')}
          value={counts.expiring7}
          sub={t('transportas.kpi.within7d')}
          tone="text-red"
          percent={pctOf(counts.expiring7)}
          ring="red"
          icon={KPI_ICON.alert}
        />
      </div>

      <Panel
        title={t('transportas.fleetHeading')}
        actions={
          <>
            <SearchInput
              value={search}
              onDebouncedChange={onSearchChange}
              placeholder={t('transportas.searchPh')}
            />
            <select
              className="field !w-auto !py-1 !text-[11px]"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                // Same reason usePagedList resets it on a search: this
                // changes how many rows there are, and asking the server for
                // rows past the end of a shorter filtered list returns an
                // empty page for a filter that plainly matches something.
                setOffset(0);
              }}
            >
              <option value="">{t('transportas.filterAllStatus')}</option>
              <option value="active">{t('transportas.status.active')}</option>
              <option value="in_service">{t('transportas.status.in_service')}</option>
              <option value="decommissioned">{t('transportas.status.decommissioned')}</option>
            </select>
            <select
              className="field !w-auto !py-1 !text-[11px]"
              value={expiryFilter}
              onChange={(e) => {
                setExpiryFilter(e.target.value);
                setOffset(0);
              }}
            >
              <option value="">{t('transportas.filterAllCompliance')}</option>
              <option value="30">{t('transportas.filterExpiring30')}</option>
              <option value="7">{t('transportas.filterExpiring7')}</option>
            </select>
            <button type="button" className="btn" onClick={() => setImporting(true)}>
              {t('import.openBtn')}
            </button>
            <button type="button" className="btn" onClick={() => setImportingGps(true)}>
              {t('import.openGpsBtn')}
            </button>
            <button type="button" className="btn" onClick={() => setFleetGpsReportOpen(true)}>
              {t('gpsReport.fleetOpenBtn')}
            </button>
            <button type="button" className="btn" onClick={() => setFleetIntegrityOpen(true)}>
              {t('fleetIntegrity.openBtn')}
            </button>
            <label className="flex items-center gap-1.5 text-[11px] text-dim">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => {
                  setShowArchived(e.target.checked);
                  setOffset(0);
                }}
              />
              {t('common.showArchived')}
            </label>
            <button type="button" className="btn btn-primary" onClick={() => setEditing({})}>
              {t('common.addBtn')}
            </button>
          </>
        }
      >
        {isError ? <ErrorState error={error} /> : null}
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : vehicles.length === 0 ? (
          <EmptyState>{search ? t('common.noMatches') : t('transportas.noneYet')}</EmptyState>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {vehicles.map((vehicle) => {
              // Computed on every render straight from data already fetched
              // for the driver <select> below — persistent and correct after
              // a refresh, unlike a warning that only appeared right after an
              // assign-driver click.
              const requiredCategory = VEHICLE_TYPE_REQUIRED_CATEGORY[vehicle.vehicle_type];
              const assignedDriver = drivers.find((d) => d.driver_id === vehicle.current_driver_id);
              const missingCategory =
                requiredCategory && assignedDriver &&
                !(assignedDriver.license_categories ?? []).includes(requiredCategory)
                  ? requiredCategory
                  : null;

              return (
              <li
                key={vehicle.vehicle_id}
                className={`rounded-2xl border border-line bg-gradient-to-b from-white/[0.045] to-white/[0.012] p-4 transition-colors hover:border-accent/25 ${
                  vehicle.soft_deleted ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* The equipment silhouette — reserved corner space for the
                      icon (or, later, a real photo) so a card is identifiable
                      at a glance before a dispatcher reads the plate. */}
                  <div
                    className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${
                      ICON_TILE_TONE[vehicle.status] ?? 'bg-white/6 text-dim'
                    }`}
                  >
                    <VehicleTypeIcon type={vehicle.vehicle_type} className="h-8 w-8" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <button
                          type="button"
                          className="truncate font-brand text-[15px] font-bold tracking-wide hover:text-accent"
                          onClick={() => setGpsReportVehicle(vehicle)}
                        >
                          {vehicle.license_plate}
                        </button>
                        {vehicle.soft_deleted ? <ArchivedBadge deletedAt={vehicle.deleted_at} /> : null}
                        <div className="truncate text-[11px] text-dim">
                          {t(`transportas.type.${vehicle.vehicle_type}`)}
                          {vehicle.make_model ? ` · ${vehicle.make_model}` : ''}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 text-[10px] font-bold uppercase ${
                          STATUS_TONE[vehicle.status] ?? 'text-dim'
                        }`}
                      >
                        {t(`transportas.status.${vehicle.status}`)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <ComplianceBadges vehicle={vehicle} />
                  {missingCategory ? (
                    <span className="rounded-full bg-amber/15 px-2 py-0.5 text-[9.5px] font-bold uppercase text-amber">
                      {t('drivers.licenseCategoryWarning', { category: missingCategory })}
                    </span>
                  ) : null}
                </div>

                <div className="mt-3">
                  <span className="mb-1 block text-[10px] uppercase tracking-[0.03em] text-dim">
                    {t('transportas.assignedDriver')}
                  </span>
                  {/* An archived vehicle takes no driver — the backend 404s
                      the assign-driver route for one, and offering the control
                      anyway turns a correct refusal into a mystery error. */}
                  <DriverAssignField
                    vehicle={vehicle}
                    drivers={drivers}
                    disabled={Boolean(vehicle.soft_deleted)}
                    onAssign={(driverId) =>
                      assignDriver.mutate({ vehicleId: vehicle.vehicle_id, driverId })
                    }
                  />
                </div>

                <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[10.5px] text-dim">
                    {vehicle.odometer_km != null ? formatKm(vehicle.odometer_km) : ''}
                  </span>
                  <div className="flex gap-1.5">
                    {/* Editing an archived vehicle is refused by the API on
                        purpose (see _load_owned_vehicle in blueprints/
                        vehicles.py) — restore it first, then edit it. */}
                    {vehicle.soft_deleted ? (
                      <button
                        type="button"
                        className="btn btn-primary !px-2.5 !py-1 !text-[11px]"
                        disabled={restoreVehicle.isPending}
                        onClick={() => restoreVehicle.mutate(vehicle.vehicle_id)}
                      >
                        {t('common.restoreBtn')}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn !px-2.5 !py-1 !text-[11px]"
                          onClick={() => setEditing(vehicle)}
                        >
                          {t('common.editBtn')}
                        </button>
                        <ConfirmButton
                          label={t('common.deleteBtn')}
                          confirmLabel={t('common.confirmDelete')}
                          hint={t('transportas.deleteVehicleHint')}
                          onConfirm={() => deleteVehicle.mutate(vehicle.vehicle_id)}
                        />
                      </>
                    )}
                  </div>
                </div>
              </li>
              );
            })}
          </ul>
        )}

        {assignDriver.isError ? <ErrorState error={assignDriver.error} /> : null}
        {deleteVehicle.isError ? <ErrorState error={deleteVehicle.error} /> : null}
        {restoreVehicle.isError ? <ErrorState error={restoreVehicle.error} /> : null}

        <PaginationFooter
          total={totalCount}
          limit={limit}
          offset={offset}
          onOffsetChange={setOffset}
          onLimitChange={setLimit}
        />
      </Panel>

      <SmartImportModal
        open={importing}
        onClose={() => setImporting(false)}
        entityType="vehicle"
        title={t('import.titleVehicles')}
      />

      <SmartImportModal
        open={importingGps}
        onClose={() => setImportingGps(false)}
        entityType="vehicle_gps_ping"
        title={t('import.titleGpsPings')}
      />

      <VehicleGpsReportModal
        open={Boolean(gpsReportVehicle)}
        onClose={() => setGpsReportVehicle(null)}
        vehicle={gpsReportVehicle}
      />

      <GpsFleetReportModal open={fleetGpsReportOpen} onClose={() => setFleetGpsReportOpen(false)} />

      <FleetIntegrityModal open={fleetIntegrityOpen} onClose={() => setFleetIntegrityOpen(false)} />

      {/* Keyed so switching straight from one vehicle's Edit to another's
          remounts the form instead of leaving the first one's values in it. */}
      {editing ? (
        <VehicleFormModal
          key={editing.vehicle_id ?? 'new'}
          vehicle={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function Kpi({ label, value, sub, tone = '', percent = 0, ring = 'accent', icon }) {
  return (
    <div className="panel flex items-center gap-3 !p-3.5">
      <KpiRing percent={percent} ring={ring} icon={icon} />
      <div className="min-w-0">
        <div className="truncate text-[10px] font-bold uppercase tracking-[0.03em] text-dim">{label}</div>
        <div className={`font-brand text-[26px] font-bold leading-tight ${tone}`}>{value ?? '—'}</div>
        {sub ? <div className="truncate text-[10.5px] text-dim">{sub}</div> : null}
      </div>
    </div>
  );
}

const RING_COLOR = {
  accent: 'var(--color-accent)',
  green: 'var(--color-green)',
  amber: 'var(--color-amber)',
  red: 'var(--color-red)',
};

/**
 * The silhouette-in-a-ring icon on each KPI card. An SVG arc rather than a
 * conic-gradient div: a stroke-dasharray circle scales cleanly and never
 * bands the way a CSS conic gradient can on some displays, and it is the
 * same technique the compliance badges' color logic already reasons about
 * (days-until thresholds → tone), just drawn instead of colored text.
 */
function KpiRing({ percent, ring, icon, size = 44 }) {
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent ?? 0));
  const color = RING_COLOR[ring] ?? RING_COLOR.accent;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} aria-hidden="true">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-line)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - clamped / 100)}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center" style={{ color }}>
        {icon}
      </div>
    </div>
  );
}
